import { base64ToCursor, compareCursor, cursorKey } from '../nats/sequence-id.js';
import {
  type ConvictionChartInterval,
  convictionChartIntervalSeconds,
} from './conviction-chart-request.js';
import { createConvictionProjection } from './conviction-projection.js';

const MAX_PPB = 1_000_000_000n;

export const CONVICTION_CHART_LIVE_DETAIL_WINDOW_MS = 60_000;

const builtChartCache = new WeakMap<
  ConvictionChartSnapshotInput,
  Map<string, SparseConvictionChart>
>();

export interface ConvictionChartStateInput {
  readonly sourceEventTs: string;
  /** NATS identity only; REST seeds and buckets deliberately omit it. */
  readonly sequenceId?: string;
  readonly anchorsPpb: ReadonlyArray<string>;
  readonly targetsPpb: ReadonlyArray<string>;
}

export interface ConvictionChartEventInput extends ConvictionChartStateInput {
  readonly bucketStart: string;
}

export interface ConvictionChartSnapshotInput {
  readonly interval: ConvictionChartInterval;
  readonly bucketSeconds: number;
  readonly from: string;
  readonly effectiveTo: string;
  readonly lastSeq: { readonly sequenceId: string | null };
  readonly seed: ConvictionChartStateInput | null;
  readonly events: ReadonlyArray<ConvictionChartEventInput>;
}

export interface BuildConvictionChartInput {
  readonly snapshot: ConvictionChartSnapshotInput;
  readonly tradingStartedAt: string;
  readonly outcomeCount: number;
  readonly retentionPerSecondPpb: string;
  readonly resolvedAt?: string | null;
}

export type ConvictionChartGeometryInput = Omit<BuildConvictionChartInput, 'snapshot'>;

export interface ParsedConvictionChartState {
  readonly sourceEventTimeMs: number;
  readonly sequenceId: string | null;
  readonly cursor: Uint8Array | null;
  readonly anchorsPpb: ReadonlyArray<bigint>;
  readonly targetsPpb: ReadonlyArray<bigint>;
  readonly source: 'opening' | 'event';
}

export interface ParsedConvictionChartEvent extends ParsedConvictionChartState {
  readonly source: 'event';
  readonly bucketStartMs: number;
}

export interface SparseConvictionChart {
  readonly interval: ConvictionChartInterval;
  readonly bucketSeconds: number;
  readonly fromMs: number;
  readonly effectiveToMs: number;
  readonly terminalAtMs: number | null;
  readonly tradingStartedAtMs: number;
  readonly outcomeCount: number;
  readonly retentionPerSecondPpb: bigint;
  readonly opening: ParsedConvictionChartState;
  readonly seed: ParsedConvictionChartState | null;
  readonly events: ReadonlyArray<ParsedConvictionChartEvent>;
  readonly states: ReadonlyArray<ParsedConvictionChartState>;
}

export interface ConvictionChartSample {
  readonly timeMs: number;
  readonly valuesPpb: ReadonlyArray<bigint>;
  readonly source: 'opening' | 'event' | 'projected';
  readonly sourceSequenceId: string | null;
}

export interface SparseConvictionChartSampler {
  sample(sampleMs: number): ConvictionChartSample;
  materialize(): ReadonlyArray<ConvictionChartSample>;
}

export class ConvictionChartContractError extends Error {
  constructor(readonly reason: string) {
    super(`Conviction chart contract failed: ${reason}`);
    this.name = 'ConvictionChartContractError';
  }
}

/**
 * REST pages contain the server-selected final state per occupied bucket, but
 * no event cursor. NATS adds cursors only to its own states. Merge overlapping
 * pages by event time, and compare cursors only when both candidates have one.
 */
export function mergeConvictionChartSnapshots(
  snapshots: ReadonlyArray<ConvictionChartSnapshotInput>,
): ConvictionChartSnapshotInput | null {
  if (snapshots.length === 0) return null;
  const ordered = [...snapshots].sort(
    (left, right) => parseTime(left.from, 'invalid_from') - parseTime(right.from, 'invalid_from'),
  );
  const first = ordered[0] as ConvictionChartSnapshotInput;
  const bucketSeconds = convictionChartIntervalSeconds(first.interval);
  const events: ParsedConvictionChartEvent[] = [];
  let effectiveToMs = parseTime(first.effectiveTo, 'invalid_effective_to');
  let latest = first;
  for (const snapshot of ordered) {
    if (snapshot.interval !== first.interval || snapshot.bucketSeconds !== bucketSeconds) {
      fail('incompatible_page');
    }
    effectiveToMs = Math.max(
      effectiveToMs,
      parseTime(snapshot.effectiveTo, 'invalid_effective_to'),
    );
    latest = snapshot;
    for (const event of snapshot.events) {
      events.push(parseEventForMerge(event, bucketSeconds));
    }
  }
  return {
    interval: first.interval,
    bucketSeconds,
    from: first.from,
    effectiveTo: new Date(effectiveToMs).toISOString(),
    lastSeq: latest.lastSeq,
    seed: first.seed,
    events: canonicalEvents(events).map(toEventInput),
  };
}

export function convictionChartGeometryRevision(
  snapshot: ConvictionChartSnapshotInput,
  input: ConvictionChartGeometryInput,
): string {
  return [
    snapshot.interval,
    snapshot.from,
    snapshot.effectiveTo,
    input.tradingStartedAt,
    String(input.outcomeCount),
    input.retentionPerSecondPpb,
    input.resolvedAt ?? '-',
    snapshot.seed === null ? '-' : stateRevision(snapshot.seed),
    ...snapshot.events.map((event) => `${event.bucketStart}:${stateRevision(event)}`),
  ].join(':');
}

export function earliestChangedConvictionChartTime(
  previous: ConvictionChartSnapshotInput,
  next: ConvictionChartSnapshotInput,
): number | null {
  if (previous.interval !== next.interval || previous.from !== next.from) return null;
  const previousByBucket = new Map(previous.events.map((event) => [event.bucketStart, event]));
  const nextByBucket = new Map(next.events.map((event) => [event.bucketStart, event]));
  let earliestMs: number | null = null;
  for (const bucket of new Set([...previousByBucket.keys(), ...nextByBucket.keys()])) {
    const previousEvent = previousByBucket.get(bucket);
    const nextEvent = nextByBucket.get(bucket);
    if (sameInputState(previousEvent, nextEvent)) continue;
    const affectedEvent = nextEvent ?? previousEvent;
    if (affectedEvent === undefined) continue;
    const eventMs = parseTime(affectedEvent.sourceEventTs, 'invalid_source_event_time');
    earliestMs = earliestMs === null ? eventMs : Math.min(earliestMs, eventMs);
  }
  if (!sameInputState(previous.seed, next.seed)) {
    const seedTimes = [previous.seed, next.seed]
      .filter((seed): seed is ConvictionChartStateInput => seed !== null)
      .map((seed) => parseTime(seed.sourceEventTs, 'invalid_source_event_time'));
    for (const seedTime of seedTimes) {
      earliestMs = earliestMs === null ? seedTime : Math.min(earliestMs, seedTime);
    }
  }
  return earliestMs;
}

export function buildSparseConvictionChart(
  input: BuildConvictionChartInput,
): SparseConvictionChart {
  const cacheKey = [
    input.tradingStartedAt,
    String(input.outcomeCount),
    input.retentionPerSecondPpb,
    input.resolvedAt ?? '-',
  ].join(':');
  const cached = builtChartCache.get(input.snapshot)?.get(cacheKey);
  if (cached !== undefined) return cached;
  const chart = buildSparseConvictionChartUncached(input);
  const snapshotCache = builtChartCache.get(input.snapshot) ?? new Map();
  snapshotCache.set(cacheKey, chart);
  builtChartCache.set(input.snapshot, snapshotCache);
  return chart;
}

function buildSparseConvictionChartUncached(
  input: BuildConvictionChartInput,
): SparseConvictionChart {
  const { snapshot } = input;
  if (
    !Number.isSafeInteger(input.outcomeCount) ||
    input.outcomeCount < 2 ||
    input.outcomeCount > 10
  ) {
    fail('invalid_outcome_count');
  }
  const expectedBucketSeconds = convictionChartIntervalSeconds(snapshot.interval);
  if (snapshot.bucketSeconds !== expectedBucketSeconds) fail('invalid_bucket_seconds');

  const tradingStartedAtMs = parseTime(input.tradingStartedAt, 'invalid_trading_start');
  const fromMs = parseTime(snapshot.from, 'invalid_from');
  const responseToMs = parseTime(snapshot.effectiveTo, 'invalid_effective_to');
  const resolvedAtMs =
    input.resolvedAt === undefined || input.resolvedAt === null
      ? null
      : parseTime(input.resolvedAt, 'invalid_resolved_at');
  const effectiveToMs = resolvedAtMs === null ? responseToMs : Math.min(responseToMs, resolvedAtMs);
  const dataToMs =
    resolvedAtMs === null
      ? responseToMs
      : Math.min(responseToMs, Math.floor(resolvedAtMs / 1_000) * 1_000 + 1_000);
  if (effectiveToMs < fromMs) fail('invalid_range');

  const retentionPerSecondPpb = parsePpb(input.retentionPerSecondPpb);
  const opening = openingState(input.outcomeCount, tradingStartedAtMs);
  const seed =
    snapshot.seed === null ? null : parseState(snapshot.seed, input.outcomeCount, 'event');
  if (seed !== null && seed.sourceEventTimeMs >= fromMs) fail('invalid_seed_time');

  const events = canonicalEvents(
    snapshot.events.map((event) => parseEvent(event, input.outcomeCount, expectedBucketSeconds)),
  );
  for (const event of events) {
    if (event.bucketStartMs < fromMs || event.bucketStartMs >= dataToMs) {
      fail('event_outside_range');
    }
    if (event.sourceEventTimeMs >= dataToMs) fail('event_outside_range');
    if (event.sourceEventTimeMs < event.bucketStartMs) fail('event_before_bucket');
    if (event.sourceEventTimeMs >= event.bucketStartMs + expectedBucketSeconds * 1_000) {
      fail('event_after_bucket');
    }
  }

  const states = canonicalStates([opening, ...(seed === null ? [] : [seed]), ...events]).filter(
    (state) => state.sourceEventTimeMs <= effectiveToMs,
  );
  return {
    interval: snapshot.interval,
    bucketSeconds: snapshot.bucketSeconds,
    fromMs,
    effectiveToMs,
    terminalAtMs: resolvedAtMs,
    tradingStartedAtMs,
    outcomeCount: input.outcomeCount,
    retentionPerSecondPpb,
    opening,
    seed,
    events,
    states,
  };
}

export function sampleSparseConvictionChart(
  chart: SparseConvictionChart,
  sampleMs: number,
): ConvictionChartSample {
  return createSample(chart, sampleMs, null);
}

export function createSparseConvictionChartSampler(
  chart: SparseConvictionChart,
): SparseConvictionChartSampler {
  const projections = new Map<
    ParsedConvictionChartState,
    ReadonlyArray<(nowMs: bigint) => bigint>
  >();
  for (const state of chart.states) {
    projections.set(
      state,
      state.anchorsPpb.map((anchorPpb, outcomeIndex) =>
        createConvictionProjection({
          anchorPpb,
          targetPpb: state.targetsPpb[outcomeIndex] as bigint,
          updatedAtSecs: BigInt(Math.floor(state.sourceEventTimeMs / 1_000)),
          retentionPerSecondPpb: chart.retentionPerSecondPpb,
        }),
      ),
    );
  }
  return {
    sample: (sampleMs) => createSample(chart, sampleMs, projections),
    materialize: () =>
      materializeWithSampler(chart, (sampleMs) => createSample(chart, sampleMs, projections)),
  };
}

function createSample(
  chart: SparseConvictionChart,
  sampleMs: number,
  projections: ReadonlyMap<
    ParsedConvictionChartState,
    ReadonlyArray<(nowMs: bigint) => bigint>
  > | null,
): ConvictionChartSample {
  if (!Number.isSafeInteger(sampleMs) || sampleMs < chart.tradingStartedAtMs) {
    fail('sample_before_opening');
  }
  const clampedMs = chart.terminalAtMs === null ? sampleMs : Math.min(sampleMs, chart.terminalAtMs);
  const state = activeStateAt(chart.states, clampedMs);
  if (state === null) fail('sample_before_opening');
  if (state.source === 'opening') {
    return {
      timeMs: clampedMs,
      valuesPpb: state.anchorsPpb,
      source: 'opening',
      sourceSequenceId: null,
    };
  }
  const projectors = projections?.get(state);
  const valuesPpb = state.anchorsPpb.map((anchorPpb, outcomeIndex) => {
    const project =
      projectors?.[outcomeIndex] ??
      createConvictionProjection({
        anchorPpb,
        targetPpb: state.targetsPpb[outcomeIndex] as bigint,
        updatedAtSecs: BigInt(Math.floor(state.sourceEventTimeMs / 1_000)),
        retentionPerSecondPpb: chart.retentionPerSecondPpb,
      });
    return project(BigInt(clampedMs));
  });
  return {
    timeMs: clampedMs,
    valuesPpb,
    source: clampedMs === state.sourceEventTimeMs ? 'event' : 'projected',
    sourceSequenceId: state.sequenceId,
  };
}

export function materializeSparseConvictionChart(
  chart: SparseConvictionChart,
): ReadonlyArray<ConvictionChartSample> {
  return createSparseConvictionChartSampler(chart).materialize();
}

function materializeWithSampler(
  chart: SparseConvictionChart,
  sample: (sampleMs: number) => ConvictionChartSample,
): ReadonlyArray<ConvictionChartSample> {
  const startMs = Math.max(chart.fromMs, chart.tradingStartedAtMs);
  if (startMs > chart.effectiveToMs) return [];
  const widthMs = chart.bucketSeconds * 1_000;
  const times = [startMs];
  let boundaryMs = (Math.floor(startMs / widthMs) + 1) * widthMs;
  while (boundaryMs < chart.effectiveToMs) {
    times.push(boundaryMs);
    boundaryMs += widthMs;
  }
  if (chart.effectiveToMs > startMs) times.push(chart.effectiveToMs);
  return times.map(sample);
}

function canonicalEvents(
  events: ReadonlyArray<ParsedConvictionChartEvent>,
): ReadonlyArray<ParsedConvictionChartEvent> {
  const byBucket = new Map<number, ParsedConvictionChartEvent>();
  for (const event of events) {
    const existing = byBucket.get(event.bucketStartMs);
    if (existing === undefined || compareState(event, existing) >= 0) {
      byBucket.set(event.bucketStartMs, event);
    }
  }
  return [...byBucket.values()].sort(
    (left, right) => left.bucketStartMs - right.bucketStartMs || compareState(left, right),
  );
}

function canonicalStates(
  states: ReadonlyArray<ParsedConvictionChartState>,
): ReadonlyArray<ParsedConvictionChartState> {
  const byIdentity = new Map<string, ParsedConvictionChartState>();
  for (const state of states) {
    const key =
      state.source === 'opening'
        ? 'opening'
        : state.cursor === null
          ? `rest:${state.sourceEventTimeMs}`
          : cursorKey(state.cursor);
    const existing = byIdentity.get(key);
    if (existing === undefined) {
      byIdentity.set(key, state);
    } else if (!sameState(existing, state)) {
      fail('conflicting_sequence_id');
    }
  }
  return [...byIdentity.values()].sort(
    (left, right) => left.sourceEventTimeMs - right.sourceEventTimeMs || compareState(left, right),
  );
}

function activeStateAt(
  states: ReadonlyArray<ParsedConvictionChartState>,
  sampleMs: number,
): ParsedConvictionChartState | null {
  let low = 0;
  let high = states.length - 1;
  let active: ParsedConvictionChartState | null = null;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const candidate = states[middle] as ParsedConvictionChartState;
    if (candidate.sourceEventTimeMs <= sampleMs) {
      active = candidate;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return active;
}

function parseEvent(
  input: ConvictionChartEventInput,
  outcomeCount: number,
  bucketSeconds: number,
): ParsedConvictionChartEvent {
  const state = parseState(input, outcomeCount, 'event');
  const bucketStartMs = parseTime(input.bucketStart, 'invalid_bucket_start');
  if (bucketStartMs % (bucketSeconds * 1_000) !== 0) fail('misaligned_bucket');
  return { ...state, source: 'event', bucketStartMs };
}

function parseEventForMerge(
  input: ConvictionChartEventInput,
  bucketSeconds: number,
): ParsedConvictionChartEvent {
  const cursor = parseOptionalCursor(input.sequenceId);
  const sourceEventTimeMs = parseWholeSecond(input.sourceEventTs);
  const bucketStartMs = parseTime(input.bucketStart, 'invalid_bucket_start');
  if (bucketStartMs % (bucketSeconds * 1_000) !== 0) fail('misaligned_bucket');
  if (
    sourceEventTimeMs < bucketStartMs ||
    sourceEventTimeMs >= bucketStartMs + bucketSeconds * 1_000
  ) {
    fail('event_outside_bucket');
  }
  return {
    sourceEventTimeMs,
    sequenceId: input.sequenceId ?? null,
    cursor,
    anchorsPpb: input.anchorsPpb.map(parsePpb),
    targetsPpb: input.targetsPpb.map(parsePpb),
    source: 'event',
    bucketStartMs,
  };
}

function toEventInput(event: ParsedConvictionChartEvent): ConvictionChartEventInput {
  return {
    bucketStart: new Date(event.bucketStartMs).toISOString(),
    sourceEventTs: new Date(event.sourceEventTimeMs).toISOString(),
    ...(event.sequenceId === null ? {} : { sequenceId: event.sequenceId }),
    anchorsPpb: event.anchorsPpb.map(String),
    targetsPpb: event.targetsPpb.map(String),
  };
}

function parseState(
  input: ConvictionChartStateInput,
  outcomeCount: number,
  source: 'event',
): ParsedConvictionChartState {
  const cursor = parseOptionalCursor(input.sequenceId);
  if (input.anchorsPpb.length !== outcomeCount || input.targetsPpb.length !== outcomeCount) {
    fail('invalid_vector_length');
  }
  return {
    sourceEventTimeMs: parseWholeSecond(input.sourceEventTs),
    sequenceId: input.sequenceId ?? null,
    cursor,
    anchorsPpb: input.anchorsPpb.map(parsePpb),
    targetsPpb: input.targetsPpb.map(parsePpb),
    source,
  };
}

function openingState(
  outcomeCount: number,
  tradingStartedAtMs: number,
): ParsedConvictionChartState {
  const count = BigInt(outcomeCount);
  const quotient = MAX_PPB / count;
  const remainder = MAX_PPB % count;
  const values = Array.from(
    { length: outcomeCount },
    (_, outcomeIndex) => quotient + (BigInt(outcomeIndex) < remainder ? 1n : 0n),
  );
  return {
    sourceEventTimeMs: tradingStartedAtMs,
    sequenceId: null,
    cursor: null,
    anchorsPpb: values,
    targetsPpb: values,
    source: 'opening',
  };
}

function compareState(left: ParsedConvictionChartState, right: ParsedConvictionChartState): number {
  if (left.cursor !== null && right.cursor !== null)
    return compareCursor(left.cursor, right.cursor);
  if (left.sourceEventTimeMs !== right.sourceEventTimeMs) {
    return left.sourceEventTimeMs - right.sourceEventTimeMs;
  }
  if (left.cursor === null) return right.cursor === null ? 0 : -1;
  return 1;
}

function parseOptionalCursor(sequenceId: string | undefined): Uint8Array | null {
  if (sequenceId === undefined) return null;
  const cursor = base64ToCursor(sequenceId);
  if (cursor === null) fail('invalid_sequence_id');
  return cursor;
}

function stateRevision(state: ConvictionChartStateInput): string {
  return [
    state.sourceEventTs,
    state.sequenceId ?? '-',
    ...state.anchorsPpb,
    ...state.targetsPpb,
  ].join(':');
}

function sameInputState(
  left: ConvictionChartStateInput | null | undefined,
  right: ConvictionChartStateInput | null | undefined,
): boolean {
  if (left === null || left === undefined || right === null || right === undefined) {
    return left === right;
  }
  return stateRevision(left) === stateRevision(right);
}

function sameState(left: ParsedConvictionChartState, right: ParsedConvictionChartState): boolean {
  return (
    left.sourceEventTimeMs === right.sourceEventTimeMs &&
    sameVector(left.anchorsPpb, right.anchorsPpb) &&
    sameVector(left.targetsPpb, right.targetsPpb)
  );
}

function sameVector(left: ReadonlyArray<bigint>, right: ReadonlyArray<bigint>): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function parseWholeSecond(value: string): number {
  const parsed = parseTime(value, 'invalid_source_event_time');
  if (parsed % 1_000 !== 0) fail('subsecond_source_event_time');
  return parsed;
}

function parseTime(value: string, reason: string): number {
  const parsed = Date.parse(value);
  if (!Number.isSafeInteger(parsed) || parsed < 0) fail(reason);
  return parsed;
}

function parsePpb(value: string): bigint {
  if (!/^\d+$/.test(value)) fail('invalid_ppb');
  const parsed = BigInt(value);
  if (parsed < 0n || parsed > MAX_PPB) fail('ppb_out_of_range');
  return parsed;
}

function fail(reason: string): never {
  throw new ConvictionChartContractError(reason);
}
